<script setup lang="ts">
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { storeToRefs } from 'pinia'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { formatCurrency, formatDate } from '../../../utilities/helpers'
import BaseInputMoney from '../../base/BaseInputMoney.vue'

import BaseButtonSecondary from '../../base/BaseButtonSecondary.vue'
import { computed, ref, watch } from 'vue'

import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import { CheckCircleIcon } from '@heroicons/vue/solid'
import { GiftIcon } from '@heroicons/vue/outline'
import { getCurrency } from '../../../utilities/usePlatformSettings'

const accountState = usePersonalAccountDataState()
const investmentProcessStore = useInvestmentProcessDataState()
const { amountToPay } = storeToRefs(investmentProcessStore)

const vouchers = computed(() => accountState?.naturalProfile?.vouchers ?? [])

const props = defineProps<{
    voucherAmount: number
    selectedVoucherId: string
}>()

const emit = defineEmits(['update:voucherAmount', 'update:selectedVoucherId', 'close'])

const voucherAmount = ref(props.voucherAmount)
const selectedVoucher = ref(vouchers.value.find((v) => v.id === props.selectedVoucherId) || vouchers.value[0])

const maxAmount = computed(() => {
    return Math.min(amountToPay.value + props.voucherAmount, selectedVoucher.value.gift_card_balance)
})

watch(
    () => selectedVoucher.value,
    (val) => {
        voucherAmount.value = maxAmount.value
    }
)

function confirm() {
    emit('update:voucherAmount', voucherAmount.value)
    emit('update:selectedVoucherId', selectedVoucher.value.id)
    emit('close')
}
</script>

<template>
    <div class="space-y-4">
        <div>
            <h2 class="mb-2 text-lg font-semibold">{{ $t('investment_process.voucher_amount') }}</h2>
            <p>{{ $t('investment_process.voucher_amount_description') }}</p>
        </div>

        <RadioGroup v-model="selectedVoucher">
            <div class="mt-4 grid grid-cols-2 gap-4">
                <RadioGroupOption
                    v-for="voucher in vouchers"
                    :key="voucher.id"
                    v-slot="{ active, checked }"
                    as="template"
                    :value="voucher">
                    <div
                        :class="[
                            active ? 'border-primary ring-2 ring-primary' : 'border-gray-300',
                            'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm transition-all focus:outline-none',
                        ]">
                        <span class="flex flex-1">
                            <div>
                                <span class="flex space-x-4">
                                    <div>
                                        <div class="inline-block shrink-0 rounded-full bg-gray-100 p-2">
                                            <GiftIcon class="h-6 w-6 text-gray-400" aria-hidden="true" />
                                        </div>
                                    </div>
                                    <div class="mt-2">
                                        <h2 class="text-xl font-medium leading-6 text-gray-900">
                                            {{ formatCurrency(voucher.gift_card_balance, getCurrency()) }}
                                        </h2>
                                    </div>
                                </span>
                                <small v-if="voucher.expiration_date" class="text-xs">
                                    {{ $t('investment_process.expires_at') }}:
                                    {{ formatDate(voucher.expiration_date) }}
                                </small>
                            </div>
                        </span>
                        <CheckCircleIcon
                            :class="[!checked ? 'invisible' : '', 'h-5 w-5 text-primary']"
                            class="transition-all"
                            aria-hidden="true" />
                        <span
                            :class="[
                                active ? 'border' : 'border-2',
                                checked ? 'border-primary' : 'border-transparent',
                                'pointer-events-none absolute -inset-px rounded-lg',
                            ]"
                            class="transition-all"
                            aria-hidden="true" />
                    </div>
                </RadioGroupOption>
            </div>
        </RadioGroup>

        <div>
            <BaseInputMoney
                v-model="voucherAmount"
                name="voucher_amount"
                currency="EUR"
                :max="maxAmount"
                label=""
                :rules="`max:${maxAmount}`"
                size="xl" />
            <p class="mt-1 text-sm text-gray-400">
                {{ $t('investment_process.voucher_available_balance') }}:
                {{ formatCurrency(selectedVoucher.gift_card_balance) }}
            </p>
        </div>
        <BaseButtonSecondary class="mt-4 w-full" size="lg" @click="confirm">{{
            $t('dialog.button.confirm')
        }}</BaseButtonSecondary>
    </div>
</template>
